var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// elements/components/x-button.ts
import { setGlobalStyle } from "../theming/global-style";
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fromEvent } from "rxjs";
import { ChemistryLitElement } from "../../chemistry-lit-element";
import { css } from "../../directives/css";
import { injectAttributesOf } from "../../directives/inject-attributes";
import { DesignSystem, elevation } from "../theming/design";
import { inputToken } from "../theming/input-token";
import { xComponents } from "../x-components";
import "./x-card";
import "./x-flex";
import "./x-link";
var buttonStyles = (host) => ({
  ":host": {
    display: "inline-block",
    borderRadius: host.sizes.borderRadius,
    whiteSpace: "nowrap",
    userSelect: "none",
    cursor: "pointer",
    "#innerwrapper": { padding: `${host.spacing.sm} ${host.spacing.md}`, flex: "1" },
    "#link": { display: "block", color: "inherit" }
  },
  ":host([mode=action])": {
    background: host.colors.accent,
    color: host.colors.on.accent,
    boxShadow: elevation[2],
    transition: "box-shadow .12s ease",
    border: `1px solid ${host.colors.accent}`,
    "#innerwrapper": {
      height: `calc(${host.baseFontSize}px + ${inputToken(host).paddingTop} + ${inputToken(host).paddingBottom})`
    }
  },
  ":host([mode=action]:hover)": { boxShadow: elevation[1] },
  ":host([mode=action]:active)": { boxShadow: "none" },
  ":host([mode=action][tone=error])": {
    background: host.colors.tone.error,
    color: host.colors.on.tone.error,
    borderColor: host.colors.tone.error
  },
  ":host([mode=action][tone=secondary]), :host([tone=secondary]:not([mode]))": {
    background: host.colors.secondary,
    color: host.colors.on.secondary,
    borderColor: host.colors.secondary
  },
  ":host([mode=text])": {
    background: "transparent",
    color: host.colors.on.white,
    "#innerwrapper": {
      padding: "0"
    },
    "a-link": { padding: 0 }
  },
  ":host([mode=landing-text])": {
    background: "transparent",
    color: host.colors.accent,
    transition: "filter .2s",
    "#innerwrapper": {
      padding: "0"
    },
    "a-link": { padding: 0 }
  },
  ":host([mode=landing-text][active])": {
    background: host.colors.accent,
    color: host.colors.on.accent,
    transition: "filter .2s",
    "#innerwrapper": {
      padding: "0"
    },
    "a-link": { padding: 0 }
  },
  ":host([mode=landing-text]:hover)": {
    filter: "grayscale(.5)"
  },
  ":host([mode=text][tone=error])": { color: host.colors.tone.error },
  ":host([mode=text][tone=success])": { color: host.colors.tone.success },
  ":host([mode=outline])": {
    background: "transparent",
    border: `1px solid ${host.colors.accent}`,
    color: host.colors.accent,
    "#innerwrapper": {
      height: `calc(${host.baseFontSize}px + ${inputToken(host).paddingTop} + ${inputToken(host).paddingBottom})`
    }
  },
  ":host([mode=outline][tone=error])": { borderColor: host.colors.tone.error, color: host.colors.tone.error },
  ":host([mode=outline][tone=success])": { borderColor: host.colors.tone.success, color: host.colors.tone.success },
  ":host([mode=outline][tone=warning])": { borderColor: host.colors.tone.warning, color: host.colors.tone.warning },
  ":host([mode=fab])": {
    background: host.colors.accent,
    color: host.colors.on.accent,
    boxShadow: elevation[2],
    "#innerwrapper": {
      height: "50px",
      width: "50px",
      justifyContent: "center",
      alignItems: "center",
      padding: "0"
    },
    [`${DesignSystem.prefix}-icon`]: {
      fontSize: "1.8em"
    }
  },
  ":host([mode=fab][tone=error])": { background: host.colors.tone.error, color: host.colors.on.tone.error },
  ":host([mode=card])": {
    border: `2px solid ${host.colors.accent}`,
    color: host.colors.accent,
    "#innerwrapper": { padding: 0 },
    [`${DesignSystem.prefix}-icon`]: { fontSize: "42px", marginRight: host.spacing.lg }
  },
  ":host([mode=icon])": {
    background: "transparent",
    borderRadius: "50%",
    "#innerwrapper": {
      height: "25px",
      width: "25px",
      justifyContent: "center",
      alignItems: "center",
      padding: "0"
    },
    [`${DesignSystem.prefix}-icon`]: {
      fontSize: "18px"
    }
  },
  ":host([mode=icon][tone=error])": {
    color: host.colors.tone.error
  },
  ":host([mode=icon][active])": {
    background: host.colors.accent,
    color: host.colors.on.accent
  },
  ":host([disabled])": { opacity: ".5", pointerEvents: "none" },
  ":host([loading])": { pointerEvents: "none", filter: "grayscale(.5)" }
});
setGlobalStyle(() => ({
  "@keyframes rotate": { from: { transform: "rotate(0)" }, to: { transform: "rotate(360deg)" } }
}));
var ElementsButton = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.icon = "";
    this.mode = "action";
    this.active = false;
    this.loading = false;
  }
  styles() {
    return [buttonStyles(this)];
  }
  get _isIconLink() {
    return this.icon && this.icon.charAt(0) === "/";
  }
  connectedCallback() {
    super.connectedCallback();
    this.subscription = fromEvent(this, "click").subscribe((e) => {
      if (!this._link)
        return;
      this._link.click();
      e.preventDefault();
      e.stopPropagation();
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.subscription?.unsubscribe();
  }
  render() {
    return xComponents(html`
      <x-link id="link" ${injectAttributesOf(this, ["class"])}>
        <x-flex id="innerwrapper" crossaxis-center center>
          ${{
      icon: this.iconDisplay(),
      fab: this.fab(),
      card: this.card()
    }[this.mode] || this.default()}
        </x-flex>
      </x-link>
    `);
  }
  iconDisplay() {
    if (!this.loading && this._isIconLink) {
      return html` <x-icon src=${this.icon} type="img"> </x-icon> `;
    }
    return html`
      <x-icon ${css(this.loading ? { animation: "2s rotate linear infinite" } : {})}>
        ${this.loading ? "refresh" : this.icon}
      </x-icon>
    `;
  }
  fab() {
    return html`
      ${this.icon ? this.iconDisplay() : nothing}
      <x-h3 on-accent><slot></slot></x-h3>
    `;
  }
  card() {
    return html`
      <x-card style="${{ width: "100%" }}">
        <x-h3 color-unset ?bold="${this.mode === "card"}">
          <x-flex horizontal crossaxis-center space="xxs">
            ${this.icon ? html` <x-icon> ${this.icon} </x-icon> ` : nothing}
            <span flex ${css({ textAlign: "center" })}>
              <slot></slot>
            </span>
          </x-flex>
        </x-h3>
      </x-card>
    `;
  }
  default() {
    return html`
      <x-flex horizontal crossaxis-center space="xxs">
        ${this.icon || this.loading ? this.iconDisplay() : nothing}
        ${this.loading ? nothing : html`
              <x-text
                ?bold="${this.mode === "card"}"
                flex
                ${css({
      textAlign: "center"
    })}
              >
                <slot></slot>
              </x-text>
            `}
      </x-flex>
    `;
  }
};
__decorateClass([
  property({ attribute: true, reflect: true })
], ElementsButton.prototype, "href", 2);
__decorateClass([
  property({ attribute: true, reflect: true })
], ElementsButton.prototype, "icon", 2);
__decorateClass([
  property({ attribute: true, reflect: true })
], ElementsButton.prototype, "mode", 2);
__decorateClass([
  property({ attribute: true, type: Boolean })
], ElementsButton.prototype, "active", 2);
__decorateClass([
  property({ attribute: true, type: Boolean, reflect: true })
], ElementsButton.prototype, "loading", 2);
ElementsButton = __decorateClass([
  customElement("x-button")
], ElementsButton);
export {
  ElementsButton,
  buttonStyles
};
